import React from "react";

export function BaseButton({
    className = "",
    disabled,
    children,
    isLoading = false,
    ...props
}) {
    return (
        <button
            type="button"
            className={`${disabled ? "opacity-55 !pointer-events-none" : ""}
      ${isLoading ? "!pointer-events-none" : ""}
      hover:transition hover:duration-300
      inline-flex align-middle flex-row items-center justify-center  
      text-sm font-semibold min-w-28 mx-1 !rounded !px-[1.5em] !py-[0.5em] border ${className}`}
            disabled={disabled}
            {...props}
        >
            {isLoading ? (
                <div role="status">
                    <svg
                        width="1em"
                        height="1em"
                        aria-hidden="true"
                        className="text-gray-200 animate-spin dark:text-white fill-teal-600"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                        />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                        />
                    </svg>
                    <span className="sr-only">Loading...</span>
                </div>
            ) : (
                children
            )}
        </button>
    );
}

export function RoundedButton({ className = "", ...props }) {
    return (
        <BaseButton
            className={`rounded-3xl ${className}`}
            {...props}
            shape={"rounded"}
        />
    );
}

// export function IconButton({ iconProps, children, className = "", ...props }) {
//   const {
//     iconName = "ErrorCircleIcon",
//     size = "1em",
//     position = "left",
//     ...otherIconProps
//   } = iconProps || {};

//   return (
//     <Button
//       {...props}
//       className={`${children ? "" : "!min-w-3 !px-[0.5em]"} ${className}`}
//     >
//       {position === "left" && (
//         <Icon
//           className={`${children ? "mr-[0.5em]" : ""} flex-shrink-0`}
//           iconName={iconName}
//           width={size}
//           height={size}
//           {...otherIconProps}
//         />
//       )}
//       {children}
//       {position === "right" && (
//         <Icon
//           className="ml-[0.5em]"
//           iconName={iconName}
//           width={size}
//           height={size}
//           {...otherIconProps}
//         />
//       )}
//     </Button>
//   );
// }

export function TextButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`border-transparent text-neutral-400 hover:text-neutral ${className}`}
            colorTheme={"text"}
        />
    );
}

export function FadedButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`border-[#e5e7eb] text-neutral-400 hover:text-neutral-700 hover:border-neutral-700 ${className}`}
            colorTheme={"faded"}
        />
    );
}

export function LightButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`bg-[#FAFAFA] border-green-500 text-[#3D4553] hover:bg-green-500 hover:text-white ${className}`}
            colorTheme={"light"}
        />
    );
}

export function SemiSolidButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`bg-primary-light border-primary text-primary font-semibold hover:bg-primary hover:text-white ${className}`}
            colorTheme={"semi-solid"}
        />
    );
}

export function SemiSolidSecondaryButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`bg-pink bg-opacity-50 bg-white !border-menumate text-menumate hover:bg-menumate hover:text-white ${className}`}
            colorTheme={"semi-solid-secondary"}
        />
    );
}

export function SolidButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`!bg-menumate text-white hover:bg-menumate border-transparent ${className}`}
            colorTheme={"solid"}
        />
    );
}

export function SolidNeutralButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`bg-neutral-800 text-white hover:bg-neutral-700 border-transparent ${className}`}
            colorTheme={"solid-neutral"}
        />
    );
}

export function SolidGrayButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`bg-light-100 text-neutral-400 hover:bg-neutral-400 hover:text-light-100 border-transparent ${className}`}
            colorTheme={"solid-gray"}
        />
    );
}

export function SolidRedButton({ className = "", ...props }) {
    return (
        <BaseButton
            {...props}
            className={`bg-danger text-white hover:bg-danger-dark border-transparent ${className}`}
            colorTheme={"red"}
        />
    );
}

export function CustomLightButton({ color = "red", ...props }) {
    return (
        <Button
            {...props}
            className={`border ${color} text-${color}`}
            colorTheme={"custom-light"}
            onMouseEnter={(ev) => {
                ev.target.style.backgroundColor = color;
                ev.target.style.color = "white";
            }}
            onMouseLeave={(ev) => {
                ev.target.style.backgroundColor = "transparent";
                ev.target.style.color = color;
            }}
        />
    );
}

export function Button({
    shape = "base",
    colorTheme = "light",
    // iconName,
    // iconProps,
    ...props
}) {
    // if (iconName || iconProps) {
    //   iconProps = { ...iconProps, iconName: iconName || iconProps?.iconName };

    //   return (
    //     <IconButton
    //       {...props}
    //       iconProps={iconProps}
    //       shape={shape}
    //       colorTheme={colorTheme}
    //     />
    //   );
    // }

    switch (colorTheme) {
        case "text":
            return <TextButton {...props} shape={shape} />;
        case "faded":
            return <FadedButton {...props} shape={shape} />;
        case "semi-solid":
            return <SemiSolidButton {...props} shape={shape} />;
        case "semi-solid-secondary":
            return <SemiSolidSecondaryButton {...props} shape={shape} />;
        case "solid":
            return <SolidButton {...props} shape={shape} />;
        case "solid-neutral":
            return <SolidNeutralButton {...props} shape={shape} />;
        case "solid-gray":
            return <SolidGrayButton {...props} shape={shape} />;
        case "red":
            return <SolidRedButton {...props} shape={shape} />;
        case "light":
            return <LightButton {...props} shape={shape} />;
        case "custom-light":
            return <CustomLightButton {...props} shape={shape} />;
        default:
            return <BaseButton {...props} />;
    }
}
